import React, { useState } from 'react';
import { connect } from 'react-redux';
import LazyLoad from 'react-lazyload';
import classNames from 'classnames';
import snakeCase from 'lodash/snakeCase';

import {
  requestAddToCart,
  requestRemoveFromCart,
} from 'services/actions/cartActions';

import { selectSubtotal } from 'selectors/cartSelectors';
import { selectTrackingData } from 'selectors/trackingSelector';

import { track, btnLocationEnum, segmentEnum } from 'utils/segment';

import UpdateCartButton from '../../Products/UpdateCartButton/UpdateCartButton';

import styles from './BoostProduct.module.css';

const BoostProduct = (props) => {
  const {
    variants,
    name,
    requestAddToCart,
    requestRemoveFromCart,
    viewSize,
    subtotal,
    trackingData,
    openLearnMoreModal,
    showRemovalWarning,
  } = props;
  const [shake, setShake] = useState(false);
  const [isLongTitle, setIsLongTitle] = useState(false);
  // boost products should only have one variant, so we use the first variant
  const variant = variants[0];
  const {
    salePrice, // BE sets salePrice to retailPrice if there is no sale price
    soldOut,
    lowInventory,
    quantity,
    quantityUpdating,
    areCartOperationsDisabled,
    id,
    parentId,
    imageUrl,
  } = variant;
  const isVariantInCart = quantity > 0;
  const isProductImageButtonDisabled =
    areCartOperationsDisabled || quantity > 1;
  const isAddingFirstQuantity = !isVariantInCart && quantityUpdating;

  function handleClickProduct() {
    const buttonName = 'PRODUCT IMAGE';
    if (isVariantInCart) {
      if (soldOut) {
        showRemovalWarning();
      } else {
        requestRemoveFromCart(
          id,
          parentId,
          btnLocationEnum.productCard,
          buttonName,
          null
        );
      }
    } else if (soldOut) {
      setShake(true);
    } else {
      requestAddToCart(
        id,
        parentId,
        btnLocationEnum.productCard,
        buttonName,
        null
      );
    }
  }

  function handleClickLearnMore() {
    openLearnMoreModal(id);
    track(segmentEnum.productViewed, {
      ...trackingData,
      experience: btnLocationEnum.learnMore,
    });
  }

  function containText(text: string, limit: number = 40) {
    if (text.length > limit) {
      // Per design, using ellipses character
      return `${text.substring(0, limit - 1)}…`;
    }
    return text;
  }

  return (
    <div
      className={classNames(styles.boostProduct, {
        [styles.soldOut]: soldOut && !isVariantInCart,
        [styles.lowInventory]: lowInventory && !soldOut && quantity === 0,
        [styles.variantInCart]: isVariantInCart || quantityUpdating,
      })}
    >
      <button
        type="button"
        className={classNames('naked-button', styles.productImageButton, {
          [styles.shake]: shake,
        })}
        disabled={isProductImageButtonDisabled}
        onClick={handleClickProduct}
        onAnimationEnd={() => {
          setShake(false);
        }}
        data-testid={`${id}-image-btn`}
        id={`${id}_${snakeCase(name)}_product_image_button`}
      >
        <span className={styles.price}>+${salePrice}</span>
        <div className={styles.imageContainer}>
          <LazyLoad once>
            <img alt={name} src={imageUrl} />
          </LazyLoad>
        </div>
      </button>
      <button
        type="button"
        onClick={() => setIsLongTitle(!isLongTitle)}
        className={classNames(styles.productName, {
          [styles.expandedTitle]: isLongTitle,
          [styles.longTitle]: name.length > 40,
        })}
      >
        {isLongTitle ? name : containText(name)}
      </button>
      <button
        type="button"
        className={classNames(styles.learnMoreButton, 'naked-button')}
        onClick={handleClickLearnMore}
      >
        Learn More
      </button>
      <span className={styles.lowInventoryCopy}>Hurry, Only a Few Left!</span>
      {(isVariantInCart || isAddingFirstQuantity) && (
        <UpdateCartButton
          productName={name}
          variant={
            isAddingFirstQuantity ? { ...variant, quantity: 1 } : variant
          }
          buttonLocation={btnLocationEnum.productCard}
          viewSize={viewSize}
          subtotal={subtotal}
          overwriteHandleClickRemove={soldOut && showRemovalWarning}
        />
      )}
    </div>
  );
};

function mapStateToProps(state, props) {
  // boost products should only have one variant, so we use the first variant
  const variant = props.variants[0];
  const { id, parentId } = variant;
  const productData = {
    productId: parentId,
    variantId: id,
    selectedCategoryId: null,
  };
  return {
    viewSize: state.viewState.viewSize,
    subtotal: selectSubtotal(state),
    trackingData: selectTrackingData(state, productData),
  };
}

export default connect(mapStateToProps, {
  requestAddToCart,
  requestRemoveFromCart,
})(BoostProduct);
